import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import Separator from "./Separator";
import Footer from "./Footer";

import logo from "./blue_logo.png";
import heroLogo from "./final_logo.png";

import locationIcon from "./location.png";
import mailIcon from "./mail.png";
import phoneIcon from "./phone-call.png";

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const nav = useMemo(
    () => [
      { label: "About", href: "#about" },
      { label: "Projects", href: "#projects" },
      { label: "Contacts", href: "#contact" },
    ],
    []
  );

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="page">
      {/* HEADER */}
      <header className="topbar">
        <div className="topbar-inner">
          <a className="brand" href="#top" onClick={closeMenu}>
            <img src={logo} alt="H.Enesta" className="brand-logo" />
          </a>

          <nav className="nav-desktop">
            {nav.map((item) => (
              <a key={item.href} className="nav-link" href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <button
            className="burger"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        {/* mobile dropdown */}
        <div className={`nav-mobile ${menuOpen ? "nav-mobile--open" : ""}`}>
          {nav.map((item) => (
            <a
              key={item.href}
              className="nav-mobile-link"
              href={item.href}
              onClick={closeMenu}
            >
              {item.label}
            </a>
          ))}
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="hero">
        <div className="hero-bg" aria-hidden="true" />

        <div className="hero-overlay" />

        <div className="hero-inner">
          <div className="hero-card">
            <img className="hero-logo" src={heroLogo} alt="H. Enesta" />
            <p className="hero-sub">
              International engineering & consulting support for nuclear energy
              projects — with primary focus on the Paks II NPP expansion in Hungary.
            </p>

            <div className="hero-actions">
              <a className="btn btn-primary" href="#contact">
                Contact us
              </a>
              <a className="btn btn-ghost" href="#projects">
                Explore focus
              </a>
            </div>

            <div className="chips">
              <span className="chip">Paks II</span>
              <span className="chip">EU Compliance</span>
              <span className="chip">Regulatory Interface</span>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="section">
        <div className="container">
          <div className="section-head">
            <h2>About H. Enesta</h2>
            <p>
              H Enesta is an international engineering and consulting company focused on
              the support of nuclear energy projects, with a primary operational emphasis
              on the Paks II nuclear power plant expansion project in Hungary.
            </p>
          </div>

          <div className="grid-2">
            <div className="glass">
              <h3>What we provide</h3>
              <p>
                Professional coordination and representative support for project participants
                operating in a highly regulated nuclear environment, ensuring full compliance
                with Hungarian and European Union regulatory frameworks.
              </p>
            </div>

            <div className="glass">
              <h3>How we work</h3>
              <ul className="fancy-list">
                <li>Clear local interface for international participants</li>
                <li>Strong focus on transparency and nuclear safety</li>
                <li>Practical, result-oriented delivery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* PROJECTS */}
      <section id="projects" className="section">
        <div className="container">
          <div className="section-head">
            <h2>Projects</h2>
            <p>
              Our activities are aligned with the regulatory environment of the Paks II project.
              We support international suppliers, contractors, and stakeholders involved in
              equipment, materials, and services delivery.
            </p>
          </div>

          <div className="stack">
            <div className="panel">
              <div className="panel-top">
                <div>
                  <h3>Our Focus: Paks II Project</h3>
                  <p className="muted">
                    We facilitate effective interaction between international project participants,
                    Hungarian regulatory authorities, and European contractors/logistics operators.
                  </p>
                </div>
                <div className="badge">Core focus</div>
              </div>

              <div className="panel-cols">
                <div className="panel-col">
                  <div className="panel-label">Interface between</div>
                  <ul className="fancy-list">
                    <li>International project participants</li>
                    <li>Hungarian regulatory & licensing authorities</li>
                    <li>European contractors & logistics operators</li>
                  </ul>
                </div>

                <div className="panel-col">
                  <div className="panel-label">Outcomes</div>
                  <ul className="fancy-list">
                    <li>Predictable coordination</li>
                    <li>Regulatory alignment</li>
                    <li>Reduced friction & delays</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid-2">
              <div className="panel">
                <h3>What We Do</h3>
                <ul className="fancy-list">
                  <li>Agency and representative support for Paks II participants</li>
                  <li>Interaction with Hungarian nuclear regulators and supervisory bodies</li>
                  <li>Support in licensing, permitting, and certification processes</li>
                  <li>Coordination of nuclear-grade equipment and material deliveries</li>
                  <li>Regulatory and administrative alignment with EU requirements</li>
                </ul>
              </div>

              <div className="panel">
                <h3>Why H Enesta</h3>
                <ul className="fancy-list">
                  <li>Clear focus on the Paks II nuclear project</li>
                  <li>Understanding of Hungarian nuclear regulatory practice</li>
                  <li>Experience in coordination of international stakeholders</li>
                  <li>Emphasis on compliance, transparency, and nuclear safety principles</li>
                  <li>Practical approach tailored to a long-term nuclear project</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* CONTACTS */}
      <section id="contact" className="section section-soft">
        <div className="container">
          <div className="section-head">
            <h2>Contacts</h2>
            <p className="muted">
              We are always ready to answer your questions and offer optimal solutions.
              Contact us using the details provided.
            </p>
          </div>

          <div className="grid-contacts">
            <div className="cardx">
              <div className="cardx-head">
                <div className="cardx-title">Company details</div>
                <div className="cardx-line" />
              </div>

              <div className="kvx">
                <div className="k">Full name</div>
                <div className="v">Limited Liability Company (Kft.) — H.Enesta</div>
              </div>
              <div className="kvx">
                <div className="k">Abbreviated name</div>
                <div className="v">H.Enesta LLC / Kft.</div>
              </div>
              <div className="kvx">
                <div className="k">Legal address</div>
                <div className="v">Revay street 4, 1065 Budapest, Hungary</div>
              </div>
              <div className="kvx">
                <div className="k">Post address</div>
                <div className="v">Revay street 4, 1065 Budapest, Hungary</div>
              </div>
              <div className="kvx">
                <div className="k">EU VAT</div>
                <div className="v">HU32780550</div>
              </div>
              <div className="kvx">
                <div className="k">Local TIN</div>
                <div className="v">32780550-2-42</div>
              </div>
              <div className="kvx">
                <div className="k">Registration No.</div>
                <div className="v">01-09-442268</div>
              </div>
              <div className="kvx">
                <div className="k">Registering authority</div>
                <div className="v">Hungary Company Register</div>
              </div>
              <div className="kvx">
                <div className="k">Head / Position</div>
                <div className="v">Amin Dzhubuev — General Director</div>
              </div>
              <div className="kvx">
                <div className="k">Authority document</div>
                <div className="v">Charter</div>
              </div>
            </div>

            <div className="cardx">
              <div className="cardx-head">
                <div className="cardx-title">Contacts</div>
                <div className="cardx-line" />
              </div>

              <div className="contact-actions">
                <a className="btn btn-primary" href="/contact.vcf">
                  Save contact
                </a>
                <div className="contact-actions-note">
                  Opens a vCard for one-tap save on iOS and Android.
                </div>
              </div>

              <div className="contactx">
                <img src={locationIcon} alt="" className="ic" />
                <div>
                  <div className="contactx-main">
                    Revay street 4, 1065 Budapest, Hungary
                  </div>
                  <div className="contactx-sub">Legal & Post address</div>
                </div>
              </div>

              <div className="contactx">
                <img src={phoneIcon} alt="" className="ic" />
                <div>
                  <a className="linkx" href="tel:+37125585393">
                    +371 25 585 393
                  </a>
                  <div className="contactx-sub">Telephone</div>
                </div>
              </div>

              <div className="contactx">
                <img src={mailIcon} alt="" className="ic" />
                <div>
                  <a className="linkx" href="mailto:amindzhubuev@gmail.com">
                    amindzhubuev@gmail.com
                  </a>
                  <div className="contactx-sub">Email</div>
                </div>
              </div>

              <div className="mini-note">
                Prefer email for official requests. We respond as soon as possible.
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
